import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';

import { InspectionTrailStop, ResourceGraphWorkspaceComponent, ResourceGraphWorkspaceRow } from './resource-graph-workspace.component';
import { ResourceTrayCardComponent } from './resource-tray-card.component';
import { DependencyTransportState, OperationalViewResourceGraphStore } from './operational-view-resource-graph.store';

import {
  OperationalEnvelope,
  OperationalSource,
  DispatchAttempt,
  JobsOperationalData,
  CloudWatchOperationalData,
  ResourceGraphNode,
  ResourceGraphReference,
  DirectDependencySummary,
  ResourceGraphTopologyManifestOperationalData,
  ResourceTenantSolutionOperationalRecord,
  TenantSolutionContextsOperationalData,
  TenantOperationsService,
} from './tenant-operations.service';

type CorollaryModule = 'jobs' | 'cloudwatch';

interface DependencyInspectionOrigin {
  node: ResourceGraphNode;
  expandedComponentIds: string[];
  dependencyExpandedComponentIds: string[];
  resourceGraphRouteStatus: ResourceGraphRouteStatus | null;
}

interface PendingResourceGraphRoute {
  componentIds: string[];
  requestVersion: number;
}

interface ResourceGraphRouteStatus {
  kind: 'not_found' | 'unavailable';
  message: string;
}

@Component({
  selector: 'app-tenant-operations',
  imports: [CommonModule, ResourceGraphWorkspaceComponent, ResourceTrayCardComponent],
  templateUrl: './tenant-operations.component.html',
  styleUrls: ['./tenant-operations.component.css'],
})
export class TenantOperationsComponent implements OnChanges, OnDestroy {
  private static readonly corollaryLiveIntervalMs = 30_000;

  @Input({ required: true }) tenantId = '';
  @Input() componentId = '';
  @Input() constructId = '';
  @Input() correlationId = '';
  selectedCorrelationId = '';

  tenantSolutionsLoading = false;
  jobsLoading = false;
  correlationSelectionLoading = false;
  cloudWatchLoading = false;
  tenantSolutionsResult: TenantSolutionContextsOperationalData | null = null;
  jobsResult: OperationalEnvelope<JobsOperationalData> | null = null;
  cloudWatchResult: OperationalEnvelope<CloudWatchOperationalData> | null = null;
  jobsCorrelations: JobsOperationalData['correlations'] = [];
  jobsAllAttempts: DispatchAttempt[] | null = null;
  jobsPage = 0;
  graphRows: ResourceGraphWorkspaceRow[] = [];
  selectedNode: ResourceGraphNode | null = null;
  activeOperationsTab = 'resource-graph';
  activeResourceFooterTab = 'resource-details';
  dependenciesExpanded = false;
  dependencyOrigins: DependencyInspectionOrigin[] = [];
  inspectionTrail: InspectionTrailStop[] = [];
  resourceGraphRouteStatus: ResourceGraphRouteStatus | null = null;
  revealingDependencyTargetId: string | null = null;

  private tenantSolutionsSubscription?: Subscription;
  private jobsSubscription?: Subscription;
  private jobsCorrelationSubscription?: Subscription;
  private cloudWatchSubscription?: Subscription;
  private corollaryLiveTimer?: number;
  private focusedCorrelationAttempts: DispatchAttempt[] = [];
  private expandedComponents = new Set<string>();
  private dependencyExpandedComponents = new Set<string>();
  private deliberateExpandedComponents: Set<string> | null = null;
  private pendingConstructId: string | null = null;
  private pendingResourceGraphRoute: PendingResourceGraphRoute | null = null;
  private pendingInspectionTarget: ResourceGraphNode | null = null;
  private resourceGraphRouteRequestVersion = 0;
  private graphStore: OperationalViewResourceGraphStore | null = null;
  private presentedManifestVersion = 0;

  constructor(private operationsService: TenantOperationsService) {}

  get resourcesLoading(): boolean { return this.graphStore?.manifestLoading ?? false; }
  get resourceGraphRefreshing(): boolean { return this.graphStore?.manifestRefreshing ?? false; }
  get resourceGraphRefreshError(): string | null { return this.graphStore?.manifestError ?? null; }
  get resourcesResult(): OperationalEnvelope<ResourceGraphTopologyManifestOperationalData> | null {
    return this.graphStore?.manifestResult ?? null;
  }

  get outgoingDependencyState(): DependencyTransportState {
    return this.dependencyEvidence('outgoing').state;
  }

  get inboundDependencyState(): DependencyTransportState {
    return this.dependencyEvidence('inbound').state;
  }

  get dependencySummaries(): DirectDependencySummary[] {
    return this.dependencyEvidence('outgoing').summaries;
  }

  get dependentSummaries(): DirectDependencySummary[] {
    return this.dependencyEvidence('inbound').summaries;
  }

  get dependencyError(): string | null {
    return this.dependencyEvidence('outgoing').error;
  }

  get inboundDependencyError(): string | null {
    return this.dependencyEvidence('inbound').error;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['tenantId']) {
      if (changes['componentId'] || changes['constructId'] || changes['correlationId']) {
        this.stopAllCorollaryLive();
        if (changes['correlationId']) {
          this.selectedCorrelationId = this.correlationId;
        }
        this.restoreRouteSelection();
      }
      return;
    }
    this.tenantSolutionsSubscription?.unsubscribe();
    this.jobsSubscription?.unsubscribe();
    this.jobsCorrelationSubscription?.unsubscribe();
    this.cloudWatchSubscription?.unsubscribe();
    this.cancelResourceGraphRouteNavigation();
    this.stopAllCorollaryLive();
    this.clearGraph();
    this.selectedCorrelationId = this.correlationId;

    if (!this.tenantId) {
      this.tenantSolutionsResult = null;
      this.tenantSolutionsLoading = false;
      this.jobsLoading = false;
      this.jobsResult = null;
      this.jobsCorrelations = [];
      this.jobsAllAttempts = null;
      this.focusedCorrelationAttempts = [];
      this.cloudWatchLoading = false;
      this.cloudWatchResult = null;
      return;
    }

    this.loadResourceGraphManifest();
    this.loadTenantSolutionContexts();
  }

  ngOnDestroy(): void {
    this.tenantSolutionsSubscription?.unsubscribe();
    this.jobsSubscription?.unsubscribe();
    this.jobsCorrelationSubscription?.unsubscribe();
    this.cloudWatchSubscription?.unsubscribe();
    this.cancelResourceGraphRouteNavigation();
    this.stopAllCorollaryLive();
    this.graphStore?.invalidate();
  }

  selectResourceFooterTab(tab: string): void {
    this.activeResourceFooterTab = tab;
    if (tab === 'jobs') {
      if (this.selectedCorrelationId) {
        this.loadJobsByCorrelation(this.selectedCorrelationId);
      } else {
        this.loadJobsForSelection();
      }
    }
    if (tab === 'cloudwatch') {
      this.loadCorrelationOptionsForSelection();
      this.loadCloudWatchInvestigation();
    }
    if (tab === 'dependencies' && this.selectedNode?.kind === 'component') {
      this.loadDirectDependencies(this.selectedNode.component_id);
    }
  }

  refreshCorollaryModule(module: CorollaryModule): void {
    if (module === 'jobs') {
      if (this.selectedCorrelationId) {
        this.loadJobsByCorrelation(this.selectedCorrelationId);
      } else {
        this.loadJobsForSelection();
      }
      return;
    }
    this.loadCloudWatchInvestigation();
  }

  corollaryStatus(module: CorollaryModule): string {
    if (this.isCorollaryLive()) {
      return 'Live';
    }
    const loading = module === 'jobs' ? this.jobsLoading : this.cloudWatchLoading;
    const result = module === 'jobs' ? this.jobsResult : this.cloudWatchResult;
    if (loading) {
      return 'Loading';
    }
    if (!result) {
      return 'Idle';
    }
    if (result.status === 'not_found') {
      return 'Empty';
    }
    if (result.status === 'error') {
      return 'Error';
    }
    return 'Ready';
  }

  isCorollaryLive(): boolean {
    return this.corollaryLiveTimer !== undefined;
  }

  setCorollaryLive(enabled: boolean): void {
    this.stopAllCorollaryLive();
    if (!enabled || !this.hasSelectedCorollaryScope()) {
      return;
    }
    this.refreshLiveCorollaryScope();
    this.corollaryLiveTimer = window.setInterval(
      () => this.refreshLiveCorollaryScope(),
      TenantOperationsComponent.corollaryLiveIntervalMs
    );
  }

  jobsAttempts(): DispatchAttempt[] {
    const attempts = (!this.selectedCorrelationId && this.jobsAllAttempts) || this.jobsResult?.data?.attempts || [];
    return attempts.slice(this.jobsPage * 25, this.jobsPage * 25 + 25);
  }

  jobsPageCount(): number {
    const attempts = (!this.selectedCorrelationId && this.jobsAllAttempts) || this.jobsResult?.data?.attempts || [];
    return Math.max(1, Math.ceil(attempts.length / 25));
  }

  setJobsPage(page: number): void {
    this.jobsPage = Math.min(Math.max(page, 0), this.jobsPageCount() - 1);
  }

  openCorrelation(correlationId: string): void {
    this.stopAllCorollaryLive();
    this.selectedCorrelationId = correlationId;
    this.jobsPage = 0;
    if (this.activeResourceFooterTab === 'jobs') {
      this.loadJobsByCorrelation(correlationId);
    }
    if (this.activeResourceFooterTab === 'cloudwatch') {
      this.loadCloudWatchInvestigation();
    }
  }

  showAllCorrelations(): void {
    this.stopAllCorollaryLive();
    if (this.selectedNode) {
      this.loadJobsForSelection();
    } else {
      this.jobsPage = 0;
    }
    this.selectedCorrelationId = '';
    if (this.activeResourceFooterTab === 'cloudwatch') {
      this.loadCloudWatchInvestigation();
    }
  }

  isCorrelationFocused(correlationId: string): boolean {
    return this.selectedCorrelationId === correlationId;
  }

  selectOperationsTab(tab: string): void {
    this.activeOperationsTab = tab;
  }

  refreshResourceGraph(): void {
    if (!this.tenantId || this.resourcesLoading || this.resourceGraphRefreshing) {
      return;
    }
    this.clearGraph(true);
    this.activeResourceFooterTab = 'resource-details';
    this.loadResourceGraphManifest(true);
  }

  sourceStatus(loading: boolean, result: OperationalEnvelope<unknown> | null): string {
    if (loading) {
      return 'Loading';
    }
    switch (result?.status) {
      case 'ready':
        return 'Ready';
      case 'not_found':
        return 'Not found';
      case 'ambiguous':
        return 'Multiple roots';
      case 'error':
        return 'Error';
      default:
        return 'Idle';
    }
  }

  tenantSolutionContextStatus(): string {
    if (this.tenantSolutionsLoading) {
      return 'Loading';
    }
    if (this.tenantSolutionsResult?.saas.error || this.tenantSolutionsResult?.resources.error) {
      return 'Error';
    }
    return this.tenantSolutionsResult?.contexts.length ? 'Loaded' : 'No records';
  }

  isBranchLoading(componentId: string): boolean {
    return this.graphStore?.detailState(componentId) === 'loading';
  }

  branchError(componentId: string): string | null {
    return this.graphStore?.detailError(componentId) ?? null;
  }

  isExpanded(componentId: string): boolean {
    return this.expandedComponents.has(componentId);
  }

  graphRecordLoadState(node: ResourceGraphNode): string {
    if (node.kind === 'construct' || this.graphStore?.detailState(node.component_id) === 'ready') {
      return 'Loaded';
    }
    if (this.graphStore?.detailState(node.component_id) === 'loading') {
      return 'Loading';
    }
    return this.graphStore?.detailState(node.component_id) === 'error' ? 'Unavailable' : 'Not loaded';
  }

  dependencyReferences(componentId: string): ResourceGraphReference[] {
    return this.graphStore?.dependencyReferences(componentId) ?? [];
  }

  toggleDependencies(): void {
    this.selectResourceFooterTab('dependencies');
  }

  retryDirectDependency(componentId: string): void {
    this.loadDirectDependencies(componentId, true);
  }

  inspectDependencyTarget(componentId: string): void {
    if (this.selectedNode) {
      if (!this.dependencyOrigins.length) {
        this.deliberateExpandedComponents = new Set(this.expandedComponents);
      }
      this.dependencyOrigins.push({
        node: this.selectedNode,
        expandedComponentIds: [...this.expandedComponents],
        dependencyExpandedComponentIds: [...this.dependencyExpandedComponents],
        resourceGraphRouteStatus: this.resourceGraphRouteStatus,
      });
    }
    const target = this.graphStore?.component(componentId);
    if (!target) {
      this.resourceGraphRouteStatus = { kind: 'not_found', message: 'This Component is not present in the current Resource Graph Topology Manifest.' };
      return;
    }
    this.pendingInspectionTarget = target;
    this.revealingDependencyTargetId = componentId;
    this.resourceGraphRouteStatus = null;
    this.loadResourceGraphRoute(componentId);
    this.rebuildInspectionTrail();
  }

  returnToDependencyOrigin(): void {
    this.selectInspectionTrailStop(this.dependencyOrigins.length - 1);
  }

  selectInspectionTrailStop(index: number): void {
    const origin = this.dependencyOrigins[index];
    if (!origin) {
      return;
    }
    this.cancelResourceGraphRouteNavigation();
    this.dependencyOrigins = this.dependencyOrigins.slice(0, index);
    this.expandedComponents = new Set(origin.expandedComponentIds);
    this.dependencyExpandedComponents = new Set(origin.dependencyExpandedComponentIds);
    this.selectNode(origin.node);
    this.resourceGraphRouteStatus = origin.resourceGraphRouteStatus;
    this.rebuildGraphRows();
    this.rebuildInspectionTrail();
  }

  selectOwnershipTreeNode(node: ResourceGraphNode): void {
    this.clearDependencyInspection();
    this.revealResourceGraphRoute(node.component_id);
    this.selectNode(node);
    this.rebuildGraphRows();
  }

  selectNode(node: ResourceGraphNode): void {
    this.stopAllCorollaryLive();
    this.selectedCorrelationId = '';
    if (this.selectedNode?.id !== node.id) {
      this.cloudWatchResult = null;
    }
    this.selectedNode = node;
    this.dependenciesExpanded = false;
    if (this.activeResourceFooterTab === 'dependencies' && node.kind === 'component') {
      this.loadDirectDependencies(node.component_id);
    }
    if (node.kind === 'component' && this.graphStore?.detailState(node.component_id) !== 'ready') {
      this.ensureComponentDetail(node.component_id);
    }
    this.loadCorrelationOptionsForSelection();
    if (this.activeResourceFooterTab === 'jobs') {
      this.loadJobsForSelection();
    }
    if (this.activeResourceFooterTab === 'cloudwatch') {
      this.loadCloudWatchInvestigation();
    }
  }

  selectReferencedComponent(reference: ResourceGraphReference): void {
    const target = this.graphStore?.component(reference.target_component_id);
    if (target) {
      this.selectNode(target);
    }
  }

  retryComponent(componentId: string): void {
    this.ensureComponentDetail(componentId, true);
  }

  inspectTenantSolution(resource: ResourceTenantSolutionOperationalRecord): void {
    this.inspectComponent(resource.component_id);
  }

  tenantSolutionManagementUrl(solutionId: string | null | undefined): string {
    return solutionId
      ? `/tenant-detail/tenant-solutions/${encodeURIComponent(solutionId)}?tenantId=${encodeURIComponent(this.tenantId)}`
      : '#';
  }

  componentIdentifier(node: ResourceGraphNode): string {
    const sortKey = node.identifiers['SK'];
    if (typeof sortKey === 'string') {
      const identifier = sortKey.split('#').at(-1);
      if (identifier) {
        return identifier;
      }
    }
    const identifier = node.component_id.split('#').at(-1);
    return identifier || node.component_id;
  }

  componentIdentifierFromId(componentId: string): string {
    return componentId.split('#').at(-1) || componentId;
  }

  toggleComponent(componentId: string): void {
    if (this.expandedComponents.has(componentId)) {
      this.expandedComponents.delete(componentId);
      this.rebuildGraphRows();
      return;
    }
    if (this.graphStore?.detailState(componentId) === 'ready') {
      this.expandedComponents.add(componentId);
      this.rebuildGraphRows();
      return;
    }
    if (this.graphStore?.detailState(componentId) === 'loading') {
      return;
    }
    this.expandedComponents.add(componentId);
    const node = this.graphStore?.component(componentId);
    if (node) {
      this.selectNode(node);
    } else {
      this.ensureComponentDetail(componentId);
    }
    this.rebuildGraphRows();
  }

  copy(value: unknown): void {
    const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  }

  private loadResourceGraphManifest(replacing = false): void {
    if (!this.graphStore) {
      this.graphStore = new OperationalViewResourceGraphStore(null, {
        manifest: () => this.operationsService.getResourceGraphManifest(this.tenantId),
        componentDetail: (componentId) => this.operationsService.getResourceNode(this.tenantId, componentId),
        dependentComponents: (componentId) => this.operationsService.findDependentComponents(this.tenantId, componentId),
      }, () => this.handleGraphStoreChange());
    }
    this.graphStore.loadManifest(replacing);
  }

  private handleGraphStoreChange(): void {
    if (this.graphStore?.manifestResult?.status === 'ready' && this.graphStore.manifestVersion !== this.presentedManifestVersion) {
      this.presentedManifestVersion = this.graphStore.manifestVersion;
      if (this.graphStore.rootComponentId) {
        this.expandedComponents.add(this.graphStore.rootComponentId);
      }
      this.restoreRouteSelection();
    }
    if (this.selectedNode?.kind === 'component') {
      this.selectedNode = this.graphStore?.component(this.selectedNode.component_id) ?? this.selectedNode;
    }
    this.selectConstructWhenAvailable(this.pendingConstructId);
    this.refreshDependencySummaries(this.selectedNode?.component_id ?? '');
    this.advanceResourceGraphRouteLoading();
    this.rebuildGraphRows();
  }

  private inspectComponent(componentId: string, constructId: string | null = null): void {
    this.stopAllCorollaryLive();
    this.restoreComponentSelection(componentId, constructId);
  }

  private loadDirectDependencies(componentId: string, force = false): void {
    this.graphStore?.loadComponentDetail(componentId);
    this.graphStore?.loadDependentComponents(componentId, force);
    this.refreshDependencySummaries(componentId);
  }

  private refreshDependencySummaries(componentId: string): void {
    if (this.selectedNode?.component_id === componentId) {
      this.rebuildGraphRows();
    }
  }

  private dependencyEvidence(direction: 'outgoing' | 'inbound') {
    const componentId = this.selectedNode?.kind === 'component' ? this.selectedNode.component_id : null;
    if (!componentId || !this.graphStore) {
      return { state: 'idle' as const, summaries: [], error: null };
    }
    return direction === 'outgoing'
      ? this.graphStore.outgoing(componentId)
      : this.graphStore.inbound(componentId);
  }

  private restoreRouteSelection(): void {
    if (this.selectedCorrelationId) {
      this.loadJobsByCorrelation(this.selectedCorrelationId);
    }
    if (this.componentId) {
      this.restoreComponentSelection(this.componentId, this.constructId || null);
    }
    if (this.activeResourceFooterTab === 'cloudwatch') {
      this.loadCloudWatchInvestigation();
    }
  }

  private restoreComponentSelection(componentId: string, constructId: string | null): void {
    if (this.selectedNode?.id !== (constructId ?? componentId)) {
      this.cloudWatchResult = null;
    }
    this.revealResourceGraphRoute(componentId);
    this.pendingConstructId = constructId;
    const node = this.graphStore?.component(componentId) ?? null;
    if (node) {
      this.selectedNode = node;
      this.resetDependencyEvidence();
      if (this.activeResourceFooterTab === 'dependencies' && node.kind === 'component') {
        this.loadDirectDependencies(node.component_id);
      }
      this.selectConstructWhenAvailable(constructId);
      if (node.kind === 'component' && this.graphStore?.detailState(componentId) !== 'ready') {
        this.ensureComponentDetail(componentId);
      }
      this.rebuildGraphRows();
      return;
    }
    this.resourceGraphRouteStatus = { kind: 'not_found', message: 'This Component is not present in the current Resource Graph Topology Manifest.' };
  }

  private selectConstructWhenAvailable(constructId: string | null): void {
    if (constructId) {
      this.selectedNode = this.graphStore?.node(constructId) ?? this.selectedNode;
    }
  }

  private resetDependencyEvidence(): void {
  }

  private loadTenantSolutionContexts(): void {
    this.tenantSolutionsLoading = true;
    this.tenantSolutionsResult = null;
    this.tenantSolutionsSubscription = this.operationsService.getTenantSolutionContexts(this.tenantId).subscribe({
      next: (result) => {
        this.tenantSolutionsResult = result;
        this.tenantSolutionsLoading = false;
      },
      error: () => {
        this.tenantSolutionsResult = {
          saas: this.networkError('saas', 'Tenant Solutions could not be retrieved'),
          resources: this.networkError('resources', 'Datastorio Tenant Solutions could not be retrieved'),
          contexts: [],
        };
        this.tenantSolutionsLoading = false;
      },
    });
  }

  private loadJobsForSelection(): void {
    const resourceId = this.selectedNode?.component_id;
    if (!resourceId) {
      this.jobsLoading = false;
      this.jobsResult = null;
      this.jobsCorrelations = [];
      this.jobsAllAttempts = null;
      return;
    }
    this.jobsLoading = true;
    this.jobsPage = 0;
    this.jobsAllAttempts = null;
    this.focusedCorrelationAttempts = [];
    this.jobsSubscription?.unsubscribe();
    this.jobsSubscription = this.operationsService.getJobsByResource(this.tenantId, resourceId).subscribe({
      next: (result) => {
        this.jobsResult = result;
        this.jobsCorrelations = result.status === 'ready' && result.data ? result.data.correlations : [];
        this.jobsLoading = false;
      },
      error: () => { this.jobsResult = this.networkError('jobs', `Dispatch Attempts for Component ${resourceId} could not be retrieved`); this.jobsLoading = false; },
    });
  }

  private loadCloudWatchInvestigation(): void {
    const context: Record<string, string> = {};
    if (this.selectedNode) {
      context['resource_id'] = this.selectedNode.component_id;
    }
    if (this.selectedCorrelationId) {
      context['correlation_id'] = this.selectedCorrelationId;
    }
    if (!Object.keys(context).length || (!this.selectedCorrelationId && this.jobsCorrelations.length > 1)) {
      this.cloudWatchLoading = false;
      this.cloudWatchResult = null;
      return;
    }
    if (this.correlationSelectionLoading) {
      this.cloudWatchLoading = false;
      return;
    }
    this.cloudWatchLoading = true;
    this.cloudWatchSubscription?.unsubscribe();
    this.cloudWatchSubscription = this.operationsService.getCloudWatchInvestigation(this.tenantId, context).subscribe({
      next: (result) => { this.cloudWatchResult = result; this.cloudWatchLoading = false; },
      error: () => { this.cloudWatchResult = this.networkError('cloudwatch', 'CloudWatch investigation could not be prepared'); this.cloudWatchLoading = false; },
    });
  }

  private loadJobsByCorrelation(correlationId: string): void {
    this.jobsLoading = true;
    this.jobsPage = 0;
    this.jobsSubscription?.unsubscribe();
    this.jobsSubscription = this.operationsService.getJobsByCorrelation(this.tenantId, correlationId).subscribe({
      next: (result) => {
        this.jobsResult = result;
        this.jobsLoading = false;
        if (result.status === 'ready' && result.data) {
          this.focusedCorrelationAttempts = result.data.attempts;
          this.loadCorrelationOptionsForSelection();
        }
      },
      error: () => { this.jobsResult = this.networkError('jobs', `Correlation ${correlationId} could not be retrieved`); this.jobsLoading = false; },
    });
  }

  private loadCorrelationOptionsForSelection(): void {
    const resourceId = this.selectedNode?.component_id;
    if (!resourceId) {
      this.jobsCorrelations = [];
      this.correlationSelectionLoading = false;
      return;
    }
    this.correlationSelectionLoading = true;
    this.jobsCorrelationSubscription?.unsubscribe();
    this.jobsCorrelationSubscription = this.operationsService.getJobsByResource(this.tenantId, resourceId).subscribe({
      next: (result) => {
        this.jobsCorrelations = result.status === 'ready' && result.data ? result.data.correlations : [];
        this.correlationSelectionLoading = false;
        if (this.activeResourceFooterTab === 'cloudwatch') {
          this.loadCloudWatchInvestigation();
        }
      },
      error: () => {
        this.jobsCorrelations = [];
        this.correlationSelectionLoading = false;
      },
    });
  }

  private ensureComponentDetail(componentId: string, force = false): void {
    this.graphStore?.loadComponentDetail(componentId, force);
  }

  private revealResourceGraphRoute(componentId: string): void {
    this.graphStore?.route(componentId)?.forEach((id) => this.expandedComponents.add(id));
  }

  private loadResourceGraphRoute(componentId: string): void {
    const requestVersion = ++this.resourceGraphRouteRequestVersion;
    this.pendingResourceGraphRoute = null;
    const cachedRoute = this.graphStore?.route(componentId) ?? null;
    if (cachedRoute) {
      this.resourceGraphRouteStatus = null;
      this.pendingResourceGraphRoute = { componentIds: cachedRoute, requestVersion };
      this.materializeResourceGraphRoute(cachedRoute);
      this.advanceResourceGraphRouteLoading();
      return;
    }
    this.revealingDependencyTargetId = null;
    this.pendingInspectionTarget = null;
    this.resourceGraphRouteStatus = {
      kind: 'not_found',
      message: 'This Component has no recorded Resource Graph Route in the current Topology Manifest.',
    };
  }

  private advanceResourceGraphRouteLoading(): void {
    const pendingRoute = this.pendingResourceGraphRoute;
    if (!pendingRoute || pendingRoute.requestVersion !== this.resourceGraphRouteRequestVersion) {
      return;
    }
    const { componentIds } = pendingRoute;
    if (!componentIds.length) {
      return;
    }
    componentIds.forEach((componentId) => {
      if (!this.expandedComponents.has(componentId)) {
        this.dependencyExpandedComponents.add(componentId);
      }
      this.expandedComponents.add(componentId);
    });
    const unloadedComponentIds = componentIds.filter((componentId) => this.graphStore?.detailState(componentId) !== 'ready');
    if (!unloadedComponentIds.length) {
      if (this.pendingResourceGraphRoute === pendingRoute) {
        this.pendingResourceGraphRoute = null;
      }
      if (this.pendingInspectionTarget) {
        this.selectNode(this.graphStore?.component(this.pendingInspectionTarget.component_id) ?? this.pendingInspectionTarget);
        this.pendingInspectionTarget = null;
        this.rebuildInspectionTrail();
      }
      this.revealingDependencyTargetId = null;
      this.rebuildGraphRows();
      return;
    }
    unloadedComponentIds
      .filter((componentId) => this.graphStore?.detailState(componentId) !== 'loading')
      .forEach((componentId) => this.ensureComponentDetail(componentId));
  }

  private materializeResourceGraphRoute(componentIds: string[]): void {
    componentIds.forEach((componentId) => {
      this.expandedComponents.add(componentId);
      this.dependencyExpandedComponents.add(componentId);
    });
    this.rebuildGraphRows();
  }

  private ownershipReferences(componentId: string): ResourceGraphReference[] {
    return this.graphStore?.ownershipReferences(componentId) ?? [];
  }

  private rebuildGraphRows(): void {
    const rows: ResourceGraphWorkspaceRow[] = [];
    const rendered = new Set<string>();
    const visit = (nodeId: string, depth: number, group: 'ownership' | 'detached'): void => {
      if (rendered.has(nodeId)) {
        return;
      }
      const node = this.graphStore?.node(nodeId);
      if (!node) {
        return;
      }
      rendered.add(nodeId);
      rows.push({
        node,
        depth,
        recordLoadState: this.graphRecordLoadState(node),
        expanded: this.isExpanded(node.component_id),
        branchLoading: this.isBranchLoading(node.component_id),
        dependencyReferences: node.kind === 'component' ? this.dependencyReferences(node.component_id) : [],
        branchError: node.kind === 'component' ? this.branchError(node.component_id) : null,
        dependencyExpanded: node.kind === 'component' && this.dependencyExpandedComponents.has(node.component_id),
        group,
      });
      if (node.kind !== 'component' || !this.expandedComponents.has(node.component_id)) {
        return;
      }
      if (this.isBranchLoading(node.component_id)) {
        rows.push({
          node: {
            id: `${node.component_id}|detail-loading`, kind: 'construct', component_id: node.component_id,
            native_type: 'Gathering resource details…', state: null, branch_capable: false,
            identifiers: {}, references: [], properties: {}, raw: [],
          },
          depth: depth + 1, recordLoadState: 'Loading', expanded: false, branchLoading: false,
          dependencyReferences: [], branchError: null, dependencyExpanded: false, detailPlaceholder: true, group,
        });
      }
      const ownedComponentIds = this.ownershipReferences(node.component_id)
        .slice()
        .sort((left, right) =>
          this.compareDisplayNames(
            left.target_native_type,
            left.target_component_id,
            right.target_native_type,
            right.target_component_id
          )
        )
        .map((reference) => reference.target_component_id);
      const constructIds = this.graphStore?.constructsFor(node.component_id)
        .slice()
        .sort((left, right) => this.compareDisplayNames(left.native_type, left.id, right.native_type, right.id))
        .map((construct) => construct.id) ?? [];
      const childIds = [...ownedComponentIds, ...constructIds].filter(
        (childId, index, candidates) =>
          candidates.indexOf(childId) === index && !rendered.has(childId) && !!this.graphStore?.node(childId)
      );
      childIds.forEach((childId) => visit(childId, depth + 1, group));
    };
    if (this.graphStore?.rootComponentId) {
      visit(this.graphStore.rootComponentId, 0, 'ownership');
    }
    this.graphStore?.detachedRootComponentIds.forEach((componentId) => visit(componentId, 0, 'detached'));
    this.graphRows = rows;
  }

  private compareDisplayNames(
    leftType: string | null | undefined,
    leftId: string,
    rightType: string | null | undefined,
    rightId: string
  ): number {
    return (leftType ?? '').localeCompare(rightType ?? '') || leftId.localeCompare(rightId);
  }

  private clearGraph(preserveCorollaryData = false): void {
    if (!preserveCorollaryData) {
      this.stopAllCorollaryLive();
    }
    this.graphStore?.invalidate();
    this.graphStore = null;
    this.presentedManifestVersion = 0;
    this.expandedComponents.clear();
    this.dependencyExpandedComponents.clear();
    this.deliberateExpandedComponents = null;
    this.pendingConstructId = null;
    this.pendingResourceGraphRoute = null;
    this.cancelResourceGraphRouteNavigation();
    this.dependenciesExpanded = false;
    this.dependencyOrigins = [];
    this.inspectionTrail = [];
    this.resourceGraphRouteStatus = null;
    this.graphRows = [];
    this.selectedNode = null;
    if (!preserveCorollaryData) {
      this.jobsSubscription?.unsubscribe();
      this.jobsCorrelationSubscription?.unsubscribe();
      this.jobsResult = null;
      this.cloudWatchResult = null;
      this.jobsCorrelations = [];
      this.jobsAllAttempts = null;
      this.focusedCorrelationAttempts = [];
      this.jobsLoading = false;
      this.cloudWatchLoading = false;
      this.correlationSelectionLoading = false;
    }
  }

  private clearDependencyInspection(): void {
    if (!this.dependencyOrigins.length) {
      return;
    }
    this.cancelResourceGraphRouteNavigation();
    this.expandedComponents = new Set(this.deliberateExpandedComponents ?? this.expandedComponents);
    this.dependencyExpandedComponents.clear();
    this.dependencyOrigins = [];
    this.deliberateExpandedComponents = null;
    this.resourceGraphRouteStatus = null;
    this.rebuildInspectionTrail();
  }

  private cancelResourceGraphRouteNavigation(): void {
    this.resourceGraphRouteRequestVersion++;
    this.pendingResourceGraphRoute = null;
  }

  private rebuildInspectionTrail(): void {
    if (!this.dependencyOrigins.length || !this.selectedNode) {
      this.inspectionTrail = [];
      return;
    }
    const originNodes = this.dependencyOrigins.map((origin) => origin.node);
    const nodes = originNodes.at(-1)?.id === this.selectedNode.id ? originNodes : [...originNodes, this.selectedNode];
    this.inspectionTrail = nodes.map((node, index) => ({
      componentId: node.component_id,
      index,
      isOrigin: index === 0,
      isCurrent: index === nodes.length - 1,
    }));
  }

  private networkError<T>(sourceName: OperationalSource['name'], message: string): OperationalEnvelope<T> {
    return {
      source: {
        name: sourceName,
        identity: { tenant_id: this.tenantId },
      },
      retrieved_at: new Date().toISOString(),
      status: 'error',
      data: null,
      error: {
        code: 'request_failed',
        message,
        context: { tenant_id: this.tenantId },
      },
    };
  }

  private hasSelectedCorollaryScope(): boolean {
    return Boolean(this.selectedNode || this.selectedCorrelationId);
  }

  private refreshLiveCorollaryScope(): void {
    this.refreshCorollaryModule('jobs');
    this.refreshCorollaryModule('cloudwatch');
  }

  private stopAllCorollaryLive(): void {
    if (this.corollaryLiveTimer !== undefined) {
      window.clearInterval(this.corollaryLiveTimer);
      this.corollaryLiveTimer = undefined;
    }
  }
}
